import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { buildApiUrl } from "../../lib/api";
import { fireTradeLoading, fireTradeToast } from "../../lib/tradeToast";
import { summarizeCancelResults } from "../../lib/queuedOrderCancellation";
import {
    applyAutomationOptimisticUpdate,
    resolveAutomationDisplay,
    resolveStrategyLabel,
} from "../../lib/positionsModel";
import "./Positions.css";

const AUTOMATION_TOGGLE_COOLDOWN_MS = 15000;

const MODE_BADGE_LABELS = {
    "auto":           "Automation On",
    "auto-approved":  "Automation On",
    "auto-pilot":     "Pilot Mode",
    "auto-degraded":  "Degraded",
    "close-only":     "Close Only",
    "preview-only":   "Preview Only",
    "manual":         "Manual Only",
};
function modeBadgeLabel(modeLabel) {
    return MODE_BADGE_LABELS[modeLabel] ?? "Manual Only";
}

const WHEEL_PHASE_LABELS = {
    sell_put:  "Selling Put",
    assigned:  "Assigned",
    sell_call: "Covered Call",
};

const QUEUED_ACTION_LABELS = {
    SELL_PUT:     "Sell Put",
    SELL_CSP:     "Sell Put",
    SELL_CALL:    "Sell Call",
    BUY_TO_CLOSE: "Close",
    TAKE_PROFIT:  "Close",
    OPEN_DEBIT_SPREAD: "Debit Spread",
};

function fmtExp(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtCurrency(n) {
    return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtOrderTime(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function buildQueuedGroupKey(order) {
    const meta = order?.meta || {};
    return [
        order?.underlying_symbol ?? "",
        order?.action ?? "",
        meta?.strike_price ?? "",
        meta?.expiration_date ?? "",
    ].join("|");
}

function resolveQueuedGroupState(group) {
    const statuses = (group?.statuses || [])
        .map((status) => String(status || "").toLowerCase())
        .filter(Boolean);

    if (statuses.includes("submitting")) {
        return {
            status: "submitting",
            label: "Submitting",
            note: "Already being submitted — cancellation is no longer available.",
        };
    }

    return {
        status: "queued",
        label: "Queued",
        note: "Will be executed at market open",
    };
}

function groupQueuedOrders(orders) {
    const map = {};
    for (const order of orders) {
        const meta = order.meta || {};
        const key  = buildQueuedGroupKey(order);
        if (!map[key]) {
            map[key] = {
                key,
                underlying_symbol: order.underlying_symbol,
                action:            order.action,
                strategy_id:       order.strategy_id ?? null,
                strategy_key:      order.strategy_key ?? null,
                strategy_label:    order.strategy_label ?? null,
                meta,
                automation_key:    order.automation_key || order.position_key || meta.position_key || order.underlying_symbol || key,
                auto_manage:       typeof order.auto_manage === "boolean" ? order.auto_manage : Boolean(meta.auto_manage),
                automation_mode:   order.automation_mode || meta.automation_mode || null,
                ids:               [],
                statuses:          [],
                total_contracts:   0,
            };
        }
        const g   = map[key];
        const qty = Number(order.qty) || 1;
        g.ids.push(order.id);
        g.statuses.push(order.status);
        g.total_contracts += qty;
        if (g.strategy_id == null && order.strategy_id != null) {
            g.strategy_id = order.strategy_id;
        }
        if (!g.strategy_key && order.strategy_key) g.strategy_key = order.strategy_key;
        if (!g.strategy_label && order.strategy_label) g.strategy_label = order.strategy_label;
    }
    return Object.values(map);
}

const cardVariants = {
    hidden:  { opacity: 0, y: 8 },
    visible: (i) => ({
        opacity: 1, y: 0,
        transition: { delay: i * 0.05, duration: 0.2, ease: "easeOut" },
    }),
};

function PositionActionConfirmModal({ modal, onConfirm, onCancel, submitting = false }) {
    if (!modal) return null;

    return (
        <motion.div
            className="pos-modal-overlay"
            onClick={submitting ? undefined : onCancel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
        >
            <motion.div
                className="pos-modal"
                onClick={(e) => e.stopPropagation()}
                initial={{ opacity: 0, scale: 0.95, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 12 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
            >
                <div className="pos-modal-title">{modal.title}</div>
                {modal.subtitle ? <div className="pos-modal-subtitle">{modal.subtitle}</div> : null}
                <div className="pos-modal-body">
                    {(modal.lines || []).map((line) => (
                        <div key={line.label} className="pos-modal-line">
                            <span className="pos-modal-line-label">{line.label}</span>
                            <span className={`pos-modal-line-val ${line.tone === "positive" ? "pos-modal-positive" : ""}`}>
                                {line.value}
                            </span>
                        </div>
                    ))}
                </div>
                {modal.note ? <div className="pos-modal-note">{modal.note}</div> : null}
                <div className="pos-modal-actions">
                    <motion.button
                        className="pos-modal-confirm"
                        onClick={onConfirm}
                        disabled={submitting}
                        whileHover={{ scale: submitting ? 1 : 1.02 }}
                        whileTap={{ scale: submitting ? 1 : 0.97 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                        {submitting ? "Working..." : modal.confirmLabel || "Confirm"}
                    </motion.button>
                    <motion.button
                        className="pos-modal-cancel"
                        onClick={onCancel}
                        disabled={submitting}
                        whileHover={{ scale: submitting ? 1 : 1.02 }}
                        whileTap={{ scale: submitting ? 1 : 0.97 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                        Back
                    </motion.button>
                </div>
            </motion.div>
        </motion.div>
    );
}

// ------------------------------------------------------------------ //
// Auto toggle button                                                   //
// ------------------------------------------------------------------ //

export function AutoToggle({ isOn, onToggle, disabled = false }) {
    return (
        <button
            className={`pos-auto-toggle ${isOn ? "on" : "off"}`}
            onClick={disabled ? undefined : onToggle}
            disabled={disabled}
            title={disabled ? "Automation unavailable" : isOn ? "Disable automation" : "Enable automation"}
        >
            {isOn ? "Automation On" : "Manual Only"}
        </button>
    );
}

// ------------------------------------------------------------------ //
// Strategy position card (Wheel positions)                            //
// ------------------------------------------------------------------ //

export function StrategyCard({ sp, index, onToggleAutomation, isToggleLocked = false }) {
    const phaseLabel = WHEEL_PHASE_LABELS[sp.current_step] || sp.current_step;
    const stratName  = sp.strategy_label || resolveStrategyLabel(sp.position_data, sp.strategy_key || sp.strategy_id, null);
    const expLabel   = fmtExp(sp.expiration_date);

    return (
        <motion.div
            className="pos-card pos-card--strategy"
            custom={index}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
        >
            <div className="pos-card-top">
                <div className="pos-card-identity">
                    <span className="pos-symbol">{sp.underlying_symbol}</span>
                    {phaseLabel && <span className="pos-step-badge">{phaseLabel}</span>}
                    {stratName && (
                        <span className="pos-step-badge pos-step-badge--strategy">{stratName}</span>
                    )}
                </div>
                <AutoToggle
                    isOn={Boolean(sp.automate)}
                    onToggle={() => onToggleAutomation(sp.strategy_id, sp.automate, "strategy", {
                        symbol: sp.underlying_symbol,
                        strategyLabel: stratName,
                        details: [
                            { label: "Symbol", value: sp.underlying_symbol || "—" },
                            { label: "Strategy", value: stratName || "Wheel" },
                            { label: "Phase", value: phaseLabel || "Open" },
                            { label: "Expires", value: expLabel || "—" },
                        ],
                    })}
                    disabled={isToggleLocked}
                />
            </div>

            {sp.contract_ticker && (
                <div className="pos-contract">{sp.contract_ticker}</div>
            )}

            {expLabel && (
                <div className="pos-detail-row">
                    <span className="pos-detail-label">Expires</span>
                    <span className="pos-detail-value">{expLabel}</span>
                </div>
            )}
        </motion.div>
    );
}

// ------------------------------------------------------------------ //
// Brokerage position card                                             //
// ------------------------------------------------------------------ //

export function BrokerageCard({ position, index, onToggleAutomation, isToggleLocked = false }) {
    const pnl         = Number(position.unrealized_pl ?? 0);
    const pnlPositive = pnl >= 0;
    const expLabel    = fmtExp(position.expiration_date);
    const strategyChip = position.strategy_label || resolveStrategyLabel(
        position.meta,
        position.strategy_key || position.strategy_chip || position.strategy_id,
        null,
    );
    const autoManage = Boolean(position.auto_manage);
    const isClosed   = position.status === "closed";

    return (
        <motion.div
            className="pos-card"
            custom={index}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
        >
            <div className="pos-card-top">
                <div className="pos-card-identity">
                    <span className="pos-symbol">{position.symbol}</span>
                    {strategyChip && (
                        <span className="pos-step-badge pos-step-badge--strategy">{strategyChip}</span>
                    )}
                </div>
                <span className={`pos-pnl ${pnlPositive ? "positive" : "negative"}`}>
                    {pnlPositive ? "+" : ""}${fmtCurrency(pnl)}
                </span>
            </div>

            <div className="pos-details">
                <div className="pos-detail-row">
                    <span className="pos-detail-label">Cost basis</span>
                    <span className="pos-detail-value">${fmtCurrency(position.cost_basis)}</span>
                </div>
                <div className="pos-detail-row">
                    <span className="pos-detail-label">Qty</span>
                    <span className="pos-detail-value">{position.qty}</span>
                </div>
                {expLabel && (
                    <div className="pos-detail-row">
                        <span className="pos-detail-label">Expires</span>
                        <span className="pos-detail-value">{expLabel}</span>
                    </div>
                )}
            </div>

            <div className="pos-card-footer">
                <AutoToggle
                    isOn={autoManage}
                    onToggle={() => onToggleAutomation(position.automation_key || position.symbol, autoManage, "brokerage", {
                        symbol: position.stock_symbol || position.symbol,
                        strategyLabel: strategyChip,
                        details: [
                            { label: "Symbol", value: position.symbol || "—" },
                            { label: "Strategy", value: strategyChip || "—" },
                            { label: "Qty", value: position.qty || "—" },
                            { label: "Expires", value: expLabel || "—" },
                        ],
                    })}
                    disabled={isClosed || isToggleLocked}
                />
            </div>
        </motion.div>
    );
}

// ------------------------------------------------------------------ //
// Trader order card                                                    //
// ------------------------------------------------------------------ //

export function TraderOrderCard({ order, index, onToggleAutomation, isToggleLocked = false }) {
    const meta          = order.meta || {};
    const strategyLabel = order.strategy_label || resolveStrategyLabel(meta, order.strategy_key || order.strategy_chip || order.strategy_id, order.action);
    const modeLabel     = resolveAutomationDisplay(order);
    const actionLabel   = QUEUED_ACTION_LABELS[order.action] || (order.legs?.length ? "Spread order" : "Order");
    const detailLine    = [
        order.strike_price != null && `$${Number(order.strike_price).toFixed(0)}`,
        fmtExp(order.expiration_date),
        order.option_type ? order.option_type.toUpperCase() : null,
        order.limit_price != null ? `Limit $${fmtCurrency(order.limit_price)}` : null,
    ].filter(Boolean).join(" · ");

    return (
        <motion.div
            className="pos-card pos-card--order"
            custom={index}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
        >
            <div className="pos-card-top">
                <div className="pos-card-identity">
                    <span className="pos-symbol">{order.symbol || order.underlying_symbol}</span>
                    <span className="pos-step-badge pos-step-badge--queued">{actionLabel}</span>
                </div>
                <span className="pos-order-status">{String(order.status || "unknown").replaceAll("_", " ")}</span>
            </div>

            {detailLine ? <div className="pos-contract">{detailLine}</div> : null}

            <div className="pos-card-footer">
                <div className="pos-card-badges">
                    {strategyLabel && (
                        <span className="pos-step-badge pos-step-badge--strategy">{strategyLabel}</span>
                    )}
                    {modeLabel && (
                        <span className={`pos-mode-badge pos-mode-badge--${modeLabel}`}>
                            {modeBadgeLabel(modeLabel)}
                        </span>
                    )}
                </div>
                <AutoToggle
                    isOn={Boolean(order.auto_manage)}
                    onToggle={() => onToggleAutomation(order.automation_key || order.position_key || order.symbol, Boolean(order.auto_manage), "recent_order", {
                        symbol: order.underlying_symbol || order.symbol,
                        strategyLabel,
                        details: [
                            { label: "Symbol", value: order.symbol || order.underlying_symbol || "—" },
                            { label: "Action", value: actionLabel || "Order" },
                            { label: "Qty", value: order.qty || "—" },
                            { label: "Expires", value: fmtExp(order.expiration_date) || "—" },
                        ],
                    })}
                    disabled={!(order.automation_key || order.position_key || order.symbol) || isToggleLocked}
                />
            </div>

            <div className="pos-details">
                <div className="pos-detail-row">
                    <span className="pos-detail-label">Submitted</span>
                    <span className="pos-detail-value">{fmtOrderTime(order.submitted_at) || "—"}</span>
                </div>
                <div className="pos-detail-row">
                    <span className="pos-detail-label">Qty</span>
                    <span className="pos-detail-value">{order.qty || "—"}</span>
                </div>
                {order.filled_qty != null && (
                    <div className="pos-detail-row">
                        <span className="pos-detail-label">Filled Qty</span>
                        <span className="pos-detail-value">{order.filled_qty}</span>
                    </div>
                )}
                {order.filled_avg_price != null && (
                    <div className="pos-detail-row">
                        <span className="pos-detail-label">Avg fill</span>
                        <span className="pos-detail-value">${fmtCurrency(order.filled_avg_price)}</span>
                    </div>
                )}
            </div>
        </motion.div>
    );
}

// ------------------------------------------------------------------ //
// Queued order card                                                    //
// ------------------------------------------------------------------ //

export function QueuedCard({ group, index, onToggleAutomation, isToggleLocked = false }) {
    const meta          = group.meta || {};
    const label         = QUEUED_ACTION_LABELS[group.action] || group.action;
    const expLabel      = fmtExp(meta.expiration_date);
    const strategyLabel = group.strategy_label || resolveStrategyLabel(meta, group.strategy_key || group.strategy_id, group.action);
    const modeLabel     = resolveAutomationDisplay(group);
    const isCancelling  = Boolean(group.isCancelling);
    const queueState    = resolveQueuedGroupState(group);
    const detailLine    = [
        meta.strike_price != null && `$${meta.strike_price}`,
        expLabel,
        meta.dte          != null && `${meta.dte}d`,
        meta.delta        != null && `Δ ${Number(meta.delta).toFixed(2)}`,
    ].filter(Boolean).join(" · ");

    return (
        <motion.div
            className="pos-card pos-card--queued"
            custom={index}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
        >
            <div className="pos-card-top">
                <div className="pos-card-identity">
                    <span className="pos-symbol">{group.underlying_symbol}</span>
                    <span className="pos-step-badge pos-step-badge--queued">{label}</span>
                    {group.total_contracts > 1 && (
                        <span className="pos-step-badge">×{group.total_contracts}</span>
                    )}
                </div>
                <span className="pos-queued-dot-label">
                    <span className={`pos-queued-dot pos-queued-dot--${queueState.status}`} />
                    {queueState.label}
                </span>
            </div>
            {detailLine && (
                <div className="pos-contract">{detailLine}</div>
            )}
            {queueState.note && (
                <div className="pos-queued-note">{queueState.note}</div>
            )}
            <div className="pos-card-footer">
                <div className="pos-card-badges">
                    {strategyLabel && (
                        <span className="pos-step-badge pos-step-badge--strategy">{strategyLabel}</span>
                    )}
                    {modeLabel && (
                        <span className={`pos-mode-badge pos-mode-badge--${modeLabel}`}>
                            {modeBadgeLabel(modeLabel)}
                        </span>
                    )}
                </div>
                <div className="pos-card-actions">
                    <button
                        type="button"
                        className="pos-cancel-btn"
                        onClick={() => group.onCancel?.(group)}
                        disabled={isCancelling || queueState.status === "submitting"}
                    >
                        {isCancelling ? "Cancelling..." : queueState.status === "submitting" ? "Submitting..." : "Cancel"}
                    </button>
                    <AutoToggle
                        isOn={Boolean(group.auto_manage)}
                        onToggle={() => onToggleAutomation(group.automation_key || group.key, Boolean(group.auto_manage), "queued", {
                            symbol: group.underlying_symbol,
                            strategyLabel,
                            actionLabel: label,
                            details: [
                                { label: "Symbol", value: group.underlying_symbol || "—" },
                                { label: "Action", value: label || "Queued trade" },
                                { label: "Contracts", value: group.total_contracts || "—" },
                                { label: "Expires", value: expLabel || "—" },
                            ],
                        })}
                        disabled={!(group.automation_key || group.key) || isToggleLocked || isCancelling || queueState.status === "submitting"}
                    />
                </div>
            </div>
        </motion.div>
    );
}

// ------------------------------------------------------------------ //
// Empty state                                                          //
// ------------------------------------------------------------------ //

function EmptyState() {
    return (
        <div className="pos-empty">
            <div className="pos-empty-title">No open positions yet</div>
            <div className="pos-empty-sub">Your first trade will appear here once executed</div>
        </div>
    );
}

// ------------------------------------------------------------------ //
// Component                                                            //
// ------------------------------------------------------------------ //

function formatSourceLabel(source, accountSource) {
    if (source === "broker") return "Broker account";
    if (source === "trader") return "Trader account";
    if (source === "broker_unavailable") return "Broker unavailable";
    return accountSource === "broker" ? "Broker mode" : "Trader mode";
}

function decorateQueuedGroups(groups) {
    return groups.map((group) => ({
        ...group,
        strategy_key: group.strategy_key || group.meta?.strategy_key || group.meta?.strategy || group.meta?.selected_strategy,
        strategy_label: group.strategy_label || group.meta?.strategy_label || group.meta?.strategy_chip || null,
        automation_key: group.automation_key || group.meta?.position_key || group.meta?.contract_ticker || group.underlying_symbol || group.key,
        auto_manage: typeof group.auto_manage === "boolean" ? group.auto_manage : Boolean(group.meta?.auto_manage),
        automation_mode: group.automation_mode || group.meta?.automation_mode || null,
    }));
}

const PositionsComponent = ({ refreshKey = 0, accountSource = "trader" }) => {
    const [positions,         setPositions]         = useState([]);
    const [recentOrders,      setRecentOrders]      = useState([]);
    const [strategyPositions, setStrategyPositions] = useState([]);
    const [queuedOrders,      setQueuedOrders]      = useState([]);
    const [queuedError,       setQueuedError]       = useState(null);
    const [loading,           setLoading]           = useState(true);
    const [error,             setError]             = useState(null);
    const [positionSource,    setPositionSource]    = useState(accountSource);
    const [automationLocks,   setAutomationLocks]   = useState({});
    const [cancellingQueued,  setCancellingQueued]  = useState({});
    const [confirmModal,      setConfirmModal]      = useState(null);
    const [confirmSubmitting, setConfirmSubmitting] = useState(false);

    const refreshQueuedOrders = async () => {
        const { data } = await axios.get(buildApiUrl("/pending-orders"), {
            withCredentials: true,
        });
        const list = data?.orders || [];
        setQueuedOrders(list);
        setQueuedError(null);
        return list;
    };

    useEffect(() => {
        const fetchPositions = axios.get(buildApiUrl("/positions"), {
            params: { account_source: accountSource },
            withCredentials: true,
        });
        const fetchStrategyPositions = axios.get(buildApiUrl("/strategy/user_positions"), {
            withCredentials: true,
        });
        const fetchQueued = axios.get(buildApiUrl("/pending-orders"), {
            withCredentials: true,
        });

        Promise.allSettled([fetchPositions, fetchStrategyPositions, fetchQueued])
            .then(([posResult, stratResult, queuedResult]) => {
                if (posResult.status === "fulfilled") {
                    setPositions(posResult.value.data.positions || []);
                    setRecentOrders(posResult.value.data.recent_orders || []);
                    setPositionSource(posResult.value.data.source || accountSource);
                } else {
                    setError(posResult.reason?.message || "Failed to load positions");
                }
                if (stratResult.status === "fulfilled") {
                    setStrategyPositions(stratResult.value.data.positions || []);
                }
                if (queuedResult.status === "fulfilled") {
                    setQueuedOrders(queuedResult.value.data.orders || []);
                    setQueuedError(null);
                } else {
                    const responseData = queuedResult.reason?.response?.data || {};
                    setQueuedOrders([]);
                    setQueuedError(
                        responseData.message ||
                        responseData.details ||
                        queuedResult.reason?.message ||
                        "Queued trades are temporarily unavailable.",
                    );
                }
            })
            .finally(() => setLoading(false));
    }, [accountSource, refreshKey]);

    // Unified toggle: handles both strategy positions (numeric id) and brokerage positions (symbol string)
    const getAutomationLock = (positionId) => {
        const until = automationLocks[positionId] || 0;
        return until > Date.now();
    };

    const releaseAutomationLockLater = (positionId, lockUntil) => {
        window.setTimeout(() => {
            setAutomationLocks((prev) => {
                if ((prev[positionId] || 0) !== lockUntil) return prev;
                const next = { ...prev };
                delete next[positionId];
                return next;
            });
        }, Math.max(0, lockUntil - Date.now()) + 50);
    };

    const runToggleAutomation = async (positionId, currentValue, type) => {
        if (getAutomationLock(positionId)) {
            const remainingMs = Math.max(0, (automationLocks[positionId] || 0) - Date.now());
            const remainingSeconds = Math.max(1, Math.ceil(remainingMs / 1000));
            fireTradeToast(`Automation was just updated. Try again in about ${remainingSeconds} seconds.`, "info");
            return;
        }

        const newValue = !currentValue;
        const lockUntil = Date.now() + AUTOMATION_TOGGLE_COOLDOWN_MS;
        setAutomationLocks((prev) => ({ ...prev, [positionId]: lockUntil }));
        releaseAutomationLockLater(positionId, lockUntil);
        fireTradeLoading(newValue ? "Turning automation on…" : "Switching this position to manual only…");

        // Optimistic update
        if (type === "strategy") {
            setStrategyPositions((prev) => applyAutomationOptimisticUpdate(prev, (sp) => sp.strategy_id === positionId, newValue, "automate"));
        } else if (type === "queued") {
            setQueuedOrders((prev) =>
                prev.map((order) =>
                    (order.automation_key || order.meta?.position_key || order.id) === positionId
                        ? { ...order, auto_manage: newValue, meta: { ...(order.meta || {}), auto_manage: newValue } }
                        : order
                )
            );
        } else if (type === "recent_order") {
            setRecentOrders((prev) =>
                prev.map((order) =>
                    (order.automation_key || order.position_key || order.symbol) === positionId
                        ? { ...order, auto_manage: newValue }
                        : order
                )
            );
        } else {
            setPositions((prev) =>
                prev.map((pos) =>
                    (pos.automation_key || pos.symbol) === positionId ? { ...pos, auto_manage: newValue } : pos
                )
            );
        }

        try {
            await axios.patch(
                buildApiUrl(`/positions/${encodeURIComponent(positionId)}/automation`),
                { auto_manage: newValue },
                { withCredentials: true }
            );
            fireTradeToast(
                newValue
                    ? "Automation is now active for this position."
                    : "Automation is off. This position is now manual only.",
                "success",
            );
        } catch (error) {
            // Revert optimistic update on failure
            if (type === "strategy") {
                setStrategyPositions((prev) => applyAutomationOptimisticUpdate(prev, (sp) => sp.strategy_id === positionId, currentValue, "automate"));
            } else if (type === "queued") {
                setQueuedOrders((prev) =>
                    prev.map((order) =>
                        (order.automation_key || order.meta?.position_key || order.id) === positionId
                            ? { ...order, auto_manage: currentValue, meta: { ...(order.meta || {}), auto_manage: currentValue } }
                            : order
                    )
                );
            } else if (type === "recent_order") {
                setRecentOrders((prev) =>
                    prev.map((order) =>
                        (order.automation_key || order.position_key || order.symbol) === positionId
                            ? { ...order, auto_manage: currentValue }
                            : order
                    )
                );
            } else {
                setPositions((prev) =>
                    prev.map((pos) =>
                        (pos.automation_key || pos.symbol) === positionId ? { ...pos, auto_manage: currentValue } : pos
                    )
                );
            }
            const responseData = error?.response?.data || {};
            const message =
                responseData?.message ||
                (typeof responseData?.details === "string" ? responseData.details : null) ||
                responseData?.error ||
                "Automation update failed. Please try again shortly.";
            fireTradeToast(message, "error");
        }
    };

    const buildAutomationConfirmModal = (positionId, currentValue, type, context = {}) => ({
        kind: "automation",
        title: "Confirm Automation",
        subtitle: "Savvy will be allowed to manage this position automatically.",
        confirmLabel: "Confirm Automation",
        note: "This only affects automated management for this trade. No order will be submitted right now.",
        lines: [
            ...(context.details || []),
            { label: "Mode", value: currentValue ? "Manual Only" : "Automation On", tone: currentValue ? undefined : "positive" },
        ],
        action: () => runToggleAutomation(positionId, currentValue, type),
    });

    const runCancelQueuedGroup = async (group) => {
        if (!group?.ids?.length || cancellingQueued[group.key]) {
            return;
        }

        setCancellingQueued((prev) => ({ ...prev, [group.key]: true }));
        fireTradeLoading("Cancelling queued trade...");

        try {
            const results = await Promise.allSettled(
                group.ids.map((id) =>
                    axios.post(
                        buildApiUrl("/cancel-order"),
                        { order_id: id },
                        { withCredentials: true }
                    )
                )
            );
            const summary = summarizeCancelResults(results);
            try {
                await refreshQueuedOrders();
                fireTradeToast(summary.message, summary.ok ? "success" : "error");
            } catch {
                fireTradeToast(
                    "Cancellation was attempted, but queued trades could not be refreshed. Please reload to confirm the current state.",
                    "error",
                );
            }
        } finally {
            setCancellingQueued((prev) => {
                const next = { ...prev };
                delete next[group.key];
                return next;
            });
        }
    };

    const buildCancelConfirmModal = (group) => {
        const meta = group?.meta || {};
        return {
            kind: "cancel",
            title: "Cancel Queued Trade",
            subtitle: "This trade will be removed from the local queue and will not be submitted at market open.",
            confirmLabel: "Cancel Trade",
            note: "Queued trades in this state have not been sent to Alpaca yet.",
            lines: [
                { label: "Symbol", value: group?.underlying_symbol || "—" },
                { label: "Action", value: QUEUED_ACTION_LABELS[group?.action] || group?.action || "Queued trade" },
                { label: "Contracts", value: group?.total_contracts || "—" },
                { label: "Expires", value: fmtExp(meta.expiration_date) || "—" },
            ],
            action: () => runCancelQueuedGroup(group),
        };
    };

    const handleToggleAutomation = (positionId, currentValue, type, context = {}) => {
        if (!currentValue) {
            setConfirmModal(buildAutomationConfirmModal(positionId, currentValue, type, context));
            return;
        }
        runToggleAutomation(positionId, currentValue, type);
    };

    const handleCancelQueuedGroup = (group) => {
        setConfirmModal(buildCancelConfirmModal(group));
    };

    const handleConfirmAction = async () => {
        if (!confirmModal?.action) return;
        setConfirmSubmitting(true);
        try {
            await confirmModal.action();
            setConfirmModal(null);
        } finally {
            setConfirmSubmitting(false);
        }
    };

    const queuedGroups = decorateQueuedGroups(groupQueuedOrders(queuedOrders)).map((group) => ({
        ...group,
        isCancelling: Boolean(cancellingQueued[group.key]),
        onCancel: handleCancelQueuedGroup,
    }));
    const hasAny = positions.length > 0 || recentOrders.length > 0 || strategyPositions.length > 0 || queuedGroups.length > 0;
    const totalCount = positions.length + recentOrders.length + strategyPositions.length + queuedGroups.length;

    return (
        <div className="positions-panel">
            <AnimatePresence>
                {confirmModal && (
                    <PositionActionConfirmModal
                        modal={confirmModal}
                        onConfirm={handleConfirmAction}
                        onCancel={() => {
                            if (!confirmSubmitting) setConfirmModal(null);
                        }}
                        submitting={confirmSubmitting}
                    />
                )}
            </AnimatePresence>

            <div className="pos-section-header">
                <span className="pos-section-title">Positions</span>
                <span className={`pos-source-badge pos-source-badge--${positionSource}`}>
                    {formatSourceLabel(positionSource, accountSource)}
                </span>
                {hasAny && (
                    <span className="pos-section-count">{totalCount}</span>
                )}
            </div>

            {loading && (
                <div className="pos-loading">Loading…</div>
            )}

            {error && !loading && (
                <div className="pos-error">{error}</div>
            )}

            {!loading && !error && (
                <>
                    {queuedGroups.length > 0 && (
                        <div className="pos-group">
                            <div className="pos-group-heading">
                                <span className="pos-group-label">Queued Trades</span>
                                <span className="pos-group-subtitle">Will be executed at market open</span>
                            </div>
                            <div className="pos-card-list">
                                {queuedGroups.map((group, i) => (
                                    <QueuedCard
                                        key={group.key}
                                        group={group}
                                        index={i}
                                        onToggleAutomation={handleToggleAutomation}
                                        isToggleLocked={getAutomationLock(group.automation_key || group.key)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {queuedError && (
                        <div className="pos-group">
                            <div className="pos-error">
                                {queuedError}
                            </div>
                        </div>
                    )}

                    {accountSource === "trader" && recentOrders.length > 0 && (
                        <div className="pos-group">
                            <span className="pos-group-label">Trade Orders</span>
                            <div className="pos-card-list">
                                {recentOrders.map((order, i) => (
                                    <TraderOrderCard
                                        key={order.id || `${order.symbol}-${order.submitted_at || i}`}
                                        order={order}
                                        index={queuedGroups.length + i}
                                        onToggleAutomation={handleToggleAutomation}
                                        isToggleLocked={getAutomationLock(order.automation_key || order.position_key || order.symbol)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {strategyPositions.length > 0 && (
                        <div className="pos-group">
                            <span className="pos-group-label">Active strategies</span>
                            <div className="pos-card-list">
                                {strategyPositions.map((sp, i) => (
                                    <StrategyCard
                                        key={`${sp.strategy_id}-${sp.underlying_symbol}`}
                                        sp={sp}
                                        index={queuedGroups.length + recentOrders.length + i}
                                        onToggleAutomation={handleToggleAutomation}
                                        isToggleLocked={getAutomationLock(sp.strategy_id)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {positions.length > 0 ? (
                        <div className="pos-card-list">
                            {positions.map((pos, i) => (
                                <BrokerageCard
                                    key={pos.symbol}
                                    position={pos}
                                    index={queuedGroups.length + recentOrders.length + strategyPositions.length + i}
                                    onToggleAutomation={handleToggleAutomation}
                                    isToggleLocked={getAutomationLock(pos.automation_key || pos.symbol)}
                                />
                            ))}
                        </div>
                    ) : (
                        !strategyPositions.length && !queuedGroups.length && !recentOrders.length && <EmptyState />
                    )}
                </>
            )}
        </div>
    );
};

export default PositionsComponent;
